import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  deleteSGRWarrantyRequest,
  updateSGRWarrantyRequest,
} from 'lib/api/sgr';
import { CURRENCIES, SGR_TYPES } from 'lib/consts';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from 'lib/designSystem/HeaderWithActions';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import { showAlert } from 'lib/functions/utils';
import { Warranty } from 'lib/types/models';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import NewQuotationDialog from './NewQuotationDialog';
import { useWarranties } from './useWarranties';
import WarrantyDetailDialog from './WarrantyDetailDialog';

type WarrantyData = Warranty & {
  documents: Document[] | null;
};

const Warranties = () => {
  const dispatch = useDispatch();

  const {
    warranties,
    isLoading,
    setIsLoading,
    openNewQuotation,
    fetchWarranties,
    handleOpenNewQuotation,
    handleCloseNewQuotation,
    newQuotation,
    updateNewQuotation,
    rawAmount,
    updateRawAmount,
    daysDifference,
    rutError,
    dateError,
    isFormValid,
    formattedAmount,
    formattedConvertedAmount,
    formattedConversionRate,
    createQuotation,
    pagination,
    setPagination,
  } = useWarranties();

  const [openWarrantyDetail, setOpenWarrantyDetail] = React.useState(false);
  const [selectedWarranty, setSelectedWarranty] =
    React.useState<WarrantyData | null>(null);

  const [stateFilter, setStateFilter] = React.useState<
    'quoted' | 'active' | 'expired' | 'claimed' | 'cancelled'
  >('active');

  const STATE_GROUPS = {
    quoted: [
      'QUOTED',
      'REQUESTED',
      'IN_REVIEW',
      'REVIEWED',
      'APPROVED',
      'REJECTED',
    ],
    active: ['ACTIVE'],
    expired: ['INACTIVE'],
    claimed: ['CLAIMED'],
    cancelled: ['CANCELLED'],
  };

  React.useEffect(() => {
    if (stateFilter) {
      void fetchWarranties({
        page: 1,
        pageSize: pagination.pageSize,
        status: STATE_GROUPS[stateFilter],
      });
    }
  }, [stateFilter]);

  const handleDeleteWarranty = async () => {
    if (!selectedWarranty?.id) return;
    await deleteSGRWarrantyRequest(dispatch, selectedWarranty?.id);
    setOpenWarrantyDetail(false);
    void fetchWarranties({
      page: pagination.page,
      pageSize: pagination.pageSize,
      status: STATE_GROUPS[stateFilter],
    });
  };

  const handleRequestWarranty = async () => {
    try {
      if (!selectedWarranty?.id) return;
      await updateSGRWarrantyRequest(dispatch, selectedWarranty?.id, {
        status: 'IN_REVIEW',
        runDecisionEngine: true,
      });
      setOpenWarrantyDetail(false);
      void fetchWarranties({
        page: pagination.page,
        pageSize: pagination.pageSize,
        status: STATE_GROUPS[stateFilter],
      });
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : 'Error al actualizar la garantía';
      showAlert(dispatch, 'error', message);
      console.error(error);
    }
  };

  const handleOpenWarrantyDetail = (warrantyId: number) => {
    const warranty = warranties.find((w: Warranty) => w.id === warrantyId);
    if (warranty) {
      setSelectedWarranty(warranty);
      setOpenWarrantyDetail(true);
    }
  };

  const handleCloseWarrantyDetail = () => {
    setOpenWarrantyDetail(false);
    setSelectedWarranty(null);
  };

  const handleStateFilterChange = (
    newFilter: 'quoted' | 'active' | 'expired' | 'claimed' | 'cancelled' | null,
  ) => {
    if (newFilter !== null) {
      setStateFilter(newFilter);
      setPagination({ ...pagination, page: 1 });
    }
  };

  const tableHeaders = [
    {
      label: 'ID',
      key: 'id',
      type: 'numeric-id',
    },
    {
      label: 'Tipo',
      key: 'translatedType',
    },
    {
      label: 'Estado',
      key: 'translatedStatus',
      type: 'status-chip',
    },
    {
      label: 'Monto Cotizado',
      key: 'quotedAmount',
      type: 'money',
    },
    {
      label: 'Moneda',
      key: 'quotedCurrency',
    },
    {
      label: 'Costo de Emisión',
      key: 'premium',
      type: 'money',
    },
    {
      label: 'Fecha Cotización',
      key: 'quoteDate',
      type: 'date',
    },
    {
      label: 'Fecha Inicio',
      key: 'startDate',
      type: 'date',
    },
    {
      label: 'Fecha Término',
      key: 'endDate',
      type: 'date',
    },
  ];

  const StateFilterTabs = () => (
    <Box>
      <ToggleButtonGroup
        value={stateFilter}
        exclusive
        onChange={(_, newFilter) => {
          handleStateFilterChange(newFilter);
        }}
        size="small"
        sx={{
          '& .MuiToggleButton-root': {
            textTransform: 'none',
            fontWeight: 500,
            minWidth: '140px',
          },
        }}
      >
        <ToggleButton value="quoted">Cotizadas</ToggleButton>
        <ToggleButton value="active">Activas</ToggleButton>
        <ToggleButton value="expired">Caducadas</ToggleButton>
        <ToggleButton value="claimed">Siniestrados</ToggleButton>
        <ToggleButton value="cancelled">Anulados</ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );

  const searchDataWithStateFilter = (params: {
    page: number;
    pageSize: number;
  }) => {
    return fetchWarranties({
      page: params.page || pagination.page,
      pageSize: params.pageSize || pagination.pageSize,
      status: STATE_GROUPS[stateFilter],
    });
  };

  const getEmptyStateMessage = () => {
    switch (stateFilter) {
      case 'quoted':
        return {
          title: 'No tienes garantías cotizadas',
          description:
            'Realiza tu primera cotización para emitir tu primera garantía',
          buttonText: 'Realizar Primera Cotización',
        };
      case 'active':
        return {
          title: 'No tienes garantías activas',
          description:
            'Las garantías activas aparecerán aquí una vez que sean aprobadas',
          buttonText: 'Ver Garantías en Proceso',
        };
      case 'expired':
        return {
          title: 'No tienes garantías caducadas',
          description: 'Las garantías inactivas aparecerán aquí',
          buttonText: 'Ver Garantías Activas',
        };
      case 'claimed':
        return {
          title: 'No tienes garantías siniestradas',
          description: 'Las garantías siniestradas aparecerán aquí',
          buttonText: 'Ver Garantías Cotizadas',
        };
      case 'cancelled':
        return {
          title: 'No tienes garantías anuladas',
          description: 'Las garantías anuladas aparecerán aquí',
          buttonText: 'Ver Garantías Cotizadas',
        };
      default:
        return {
          title: 'No tienes ninguna garantía emitida',
          description:
            'Realiza tu primera cotización para emitir tu primera garantía',
          buttonText: 'Realizar Primera Cotización',
        };
    }
  };

  const handleEmptyStateAction = () => {
    switch (stateFilter) {
      case 'quoted':
        handleOpenNewQuotation();
        break;
      case 'active':
        setStateFilter('quoted');
        break;
      case 'expired':
        setStateFilter('active');
        break;
      case 'claimed':
        setStateFilter('quoted');
        break;
      case 'cancelled':
        setStateFilter('quoted');
        break;
      default:
        handleOpenNewQuotation();
    }
  };

  const buttons: CustomButtonProps[] = [
    {
      name: 'Nueva Garantía',
      color: 'primary',
      action: handleOpenNewQuotation,
    },
  ];

  useEffect(() => {
    void fetchWarranties({
      page: 1,
      pageSize: pagination.pageSize,
      status: STATE_GROUPS[stateFilter],
    });
  }, [stateFilter, fetchWarranties, pagination.pageSize]);

  if (isLoading) return <CenteredCircularProgress />;

  return (
    <>
      <HeaderWithActions
        title=""
        variant="body1"
        buttons={buttons}
        customTitle={<StateFilterTabs />}
      />

      {warranties.length !== 0 ? (
        <TableWithPagination
          data={warranties}
          setData={searchDataWithStateFilter}
          dataHeaders={tableHeaders}
          buttons={[]}
          filters={[
            {
              key: 'type',
              label: 'Tipo',
              options: SGR_TYPES.map((type) => type.value),
              type: 'select',
            },
          ]}
          caption=""
          handleClickRow={handleOpenWarrantyDetail}
          showActions
          pagination={pagination}
          setPagination={setPagination}
          searchData={searchDataWithStateFilter}
          setLoading={setIsLoading}
          exportToFile="xlsx"
          lastElement
        />
      ) : (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
          flexDirection="column"
          gap={2}
        >
          <Typography variant="h4" gutterBottom color="text.primary">
            {getEmptyStateMessage().title}
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            {getEmptyStateMessage().description}
          </Typography>
          <CustomButton
            color="primary"
            onClick={handleEmptyStateAction}
            size="large"
          >
            {getEmptyStateMessage().buttonText}
          </CustomButton>
        </Box>
      )}

      <NewQuotationDialog
        open={openNewQuotation}
        onClose={handleCloseNewQuotation}
        onSubmit={() => void createQuotation()}
        newQuotation={newQuotation}
        updateNewQuotation={updateNewQuotation}
        rawAmount={rawAmount}
        updateRawAmount={updateRawAmount}
        daysDifference={daysDifference}
        rutError={rutError}
        dateError={dateError}
        isFormValid={isFormValid}
        CURRENCIES={CURRENCIES}
        formattedAmount={formattedAmount}
        formattedConvertedAmount={formattedConvertedAmount}
        formattedConversionRate={formattedConversionRate}
      />

      <WarrantyDetailDialog
        open={openWarrantyDetail}
        onClose={handleCloseWarrantyDetail}
        warranty={selectedWarranty}
        onDelete={() => void handleDeleteWarranty()}
        onRequest={() => void handleRequestWarranty()}
      />
    </>
  );
};

export default Warranties;
